import { useId } from "react";
import { useTranslation } from "react-i18next";

import BaseTextArea from "@/components_v2/FormFields/Input/BaseTextArea";
import AnswerVerdictTag from "@/domains/certifications/components/AnswerVerdictTag";
import { formatAnswerValue } from "@/features/certifications/knowledge-check-flags/utils";
import { AnswerReviewProps } from "@/features/certifications/knowledge-check-flags/types";
import { ANSWER_VERDICTS } from "@/domains/certifications/constants";

import "../styles.css";

const AnswerReview = ({
  answer,
  position,
  title,
  verdict,
  feedback,
  readOnly,
  onVerdictChange,
  onFeedbackChange,
}: AnswerReviewProps) => {
  const { t } = useTranslation();
  const questionId = useId();
  const feedbackId = useId();

  return (
    <li className="kc-answer flex flex-col gap-300">
      <div className="flex flex-wrap items-start justify-between gap-200">
        <span id={questionId} className="kc-question body-m-medium">
          {title
            ? t("certification.admin.knowledgeCheckFlags.questionWithTitle", {
                position,
                title,
              })
            : t("certification.admin.knowledgeCheckFlags.question", {
                position,
              })}
        </span>

        <AnswerVerdictTag
          value={verdict}
          aiVerdict={answer.aiVerdict}
          onChange={onVerdictChange}
          readOnly={readOnly}
          labelledBy={questionId}
        />
      </div>

      <p
        className={`kc-answer-text body-s-regular ${
          verdict === ANSWER_VERDICTS.INCORRECT
            ? "kc-answer-text-incorrect"
            : ""
        }`}
      >
        {formatAnswerValue(answer.value) ||
          t("certification.admin.knowledgeCheckFlags.noAnswer")}
      </p>

      <div className="kc-ai-review flex flex-col gap-100">
        <span className="kc-ai-label body-xs-regular">
          {t("certification.admin.knowledgeCheckFlags.aiReviewLabel")}
        </span>

        <p className="kc-ai-text body-s-regular">
          {answer.aiRationale ||
            t("certification.admin.knowledgeCheckFlags.noAiReview")}
        </p>
      </div>

      {readOnly ? (
        feedback && (
          <div className="flex flex-col gap-100">
            <span className="kc-eyebrow body-xs-regular">
              {t("certification.admin.knowledgeCheckFlags.yourFeedback")}
            </span>

            <p className="kc-strong body-s-regular">{feedback}</p>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-100">
          <label htmlFor={feedbackId} className="kc-eyebrow body-xs-regular">
            {t("certification.admin.knowledgeCheckFlags.yourFeedback")}
          </label>

          <BaseTextArea
            id={feedbackId}
            aria-describedby={questionId}
            value={feedback}
            autoSize={{ minRows: 2, maxRows: 5 }}
            placeholder={t(
              "certification.admin.knowledgeCheckFlags.feedbackPlaceholder",
            )}
            onChange={(event) => onFeedbackChange(event.target.value)}
          />
        </div>
      )}
    </li>
  );
};

export default AnswerReview;
